import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SupplierBillService } from '../../services/supplierBill.service';
import { SupplierBill, BillEditDTO } from '../../interfaces/procurement/supplierBill.interface';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';
import { AuthService } from '../../services/auth.service';
import TaxCodeSelect from '../tax/TaxCodeSelect';
import { hasTaxCodes, previewTax, useTaxCodeLookup } from '../tax/useTaxCodeLookup';
import { formatFc, formatFcPrice, isForeignCurrency, toastSaved } from '../../utils/fcDocuments'; // G5a
import { formatMoney } from '../../utils/format';

export const SupplierBillEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [bill, setBill] = useState<SupplierBill | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [billDate, setBillDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [supplierInvoiceRef, setSupplierInvoiceRef] = useState('');
  const [taxAmount, setTaxAmount] = useState<number>(0);
  const [editReason, setEditReason] = useState('');
  // Per-line editable unit price keyed by goodsReceiptLineId
  const [prices, setPrices] = useState<Record<string, number>>({});
  // Per-line tax code keyed by goodsReceiptLineId (null = uncoded)
  const [codes, setCodes] = useState<Record<string, string | null>>({});
  const taxLookup = useTaxCodeLookup(AuthService.getSelectedInstitution());
  // ---- G5a: a foreign PO bill edits its prices in the bill currency and its AP rate; no tax ----
  const foreign = !!bill && !!bill.purchaseOrderId && isForeignCurrency(bill.currencyCode);
  const [rate, setRate] = useState<number | ''>('');
  // ---- end G5a ----
  const showTaxCodes = hasTaxCodes(taxLookup) && !foreign;
  const anyCoded = Object.values(codes).some(Boolean);

  useEffect(() => {
    if (!id) return;
    SupplierBillService.getById(id)
      .then(b => {
        setBill(b);
        setBillDate(b.billDate);
        setDueDate(b.dueDate);
        setSupplierInvoiceRef(b.supplierInvoiceRef ?? '');
        setTaxAmount(b.taxAmount ?? 0);
        const fcBill = !!b.purchaseOrderId && isForeignCurrency(b.currencyCode); // G5a
        if (fcBill) setRate(b.exchangeRate ?? '');
        const initialPrices: Record<string, number> = {};
        b.lines.forEach(l => {
          initialPrices[l.goodsReceiptLineId] = fcBill ? (l.foreignUnitPrice ?? l.unitPrice) : l.unitPrice;
        });
        setPrices(initialPrices);
        const initialCodes: Record<string, string | null> = {};
        b.lines.forEach(l => { initialCodes[l.goodsReceiptLineId] = l.taxCodeId ?? null; });
        setCodes(initialCodes);
      })
      .catch(e => toast.showApiError(e, 'Failed to load supplier bill'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!bill) return;
    if (editReason.trim().length === 0) {
      toast.showApiError(null, 'A reason is required');
      return;
    }
    if (foreign && !(Number(rate) > 0)) { toast.showApiError(null, `Enter the ${bill.currencyCode} rate`); return; }
    if (bill.lines.some(l => !((prices[l.goodsReceiptLineId] ?? l.unitPrice) > 0))) {
      toast.showApiError(null, 'Enter a positive bill unit price for every line');
      return;
    }
    setSaving(true);
    try {
      const dto: BillEditDTO = {
        billDate,
        dueDate,
        supplierInvoiceRef: supplierInvoiceRef || undefined,
        editReason: editReason.trim(),
        taxAmount: !foreign && !anyCoded && taxAmount ? taxAmount : undefined,
        lines: bill.lines.map(l => ({
          goodsReceiptLineId: l.goodsReceiptLineId,
          unitPrice: prices[l.goodsReceiptLineId] ?? l.unitPrice,
          taxCodeId: foreign ? null : (codes[l.goodsReceiptLineId] || null),
        })),
        // G5a: only a changed rate is sent (absent keeps the stored one)
        ...(foreign && Number(rate) !== Number(bill.exchangeRate) ? { exchangeRate: Number(rate) } : {}),
      };
      const updated = await SupplierBillService.edit(bill.id, dto);
      toastSaved(toast, 'Bill updated', updated.warnings);
      navigate(`/procurement/supplier-bills/${updated.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to update bill');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!bill) return <Box sx={{ p: 3 }}><Typography>Supplier bill not found.</Typography></Box>;

  return (
    <Box sx={{ p: 3 }} data-help-page="procurement.billEdit.page">
      <Typography variant="h5" sx={{ mb: 3 }}>Edit Supplier Bill — {bill.billNumber}</Typography>

      <Paper sx={{ p: 3, mb: 3 }} data-help-section="procurement.billEdit.section.header">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            data-help="procurement.billEdit.field.billDate"
            label="Bill Date"
            type="date"
            value={billDate}
            onChange={e => setBillDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            data-help="procurement.billEdit.field.dueDate"
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            data-help="procurement.billEdit.field.invoiceRef"
            label="Supplier Invoice Ref"
            value={supplierInvoiceRef}
            onChange={e => setSupplierInvoiceRef(e.target.value)}
          />

          {foreign && (
            <TextField
              data-help="procurement.billEdit.field.rate"
              label={`Bill rate (BDT per ${bill.currencyCode})`}
              type="number"
              value={rate}
              onChange={e => setRate(e.target.value === '' ? '' : Number(e.target.value))}
              inputProps={{ min: 0, step: 'any' }}
              helperText="The gap to the receipt rates is re-posted to FX gain / loss. No tax on a foreign-currency PO bill."
            />
          )}
          {!foreign && (
          <TextField
            data-help="procurement.billEdit.field.tax"
            label="Tax Amount"
            type="number"
            value={taxAmount}
            onChange={e => setTaxAmount(Number(e.target.value))}
            inputProps={{ step: 0.01, min: 0 }}
            disabled={anyCoded}
            helperText={anyCoded ? 'Tax is calculated from the line tax codes' : undefined}
          />
          )}

          <TextField
            data-help="procurement.billEdit.field.reason"
            label="Reason for edit"
            value={editReason}
            onChange={e => setEditReason(e.target.value)}
            multiline
            minRows={2}
            required
            error={editReason.trim().length === 0 && editReason.length > 0}
            helperText="Required — explain why this bill is being edited"
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }} data-help-section="procurement.billEdit.lines">
        <Typography variant="h6" sx={{ mb: 2 }}>Bill Lines</Typography>
        <ScrollableTable>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell data-help="procurement.billForm.col.item">Item</TableCell>
                  <TableCell data-help="procurement.billEdit.col.qty">Quantity</TableCell>
                  <TableCell data-help="procurement.billForm.col.poPrice">PO Unit Price{foreign ? ` (${bill.currencyCode})` : ''}</TableCell>
                  <TableCell data-help="procurement.billForm.col.billPrice">Bill Unit Price{foreign ? ` (${bill.currencyCode})` : ''}</TableCell>
                  {showTaxCodes && <TableCell data-help="procurement.billForm.col.taxCode">Tax code</TableCell>}
                  <TableCell data-help="procurement.billForm.col.value">Line Value</TableCell>
                  {showTaxCodes && <TableCell data-help="procurement.billForm.col.tax">Tax</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {bill.lines.map(line => {
                  const rawPrice = prices[line.goodsReceiptLineId] ?? line.unitPrice;
                  const billPrice = Number.isFinite(rawPrice) ? rawPrice : NaN;
                  return (
                    <TableRow key={line.id}>
                      <TableCell>{line.itemName ?? '—'}{line.itemCode ? ` (${line.itemCode})` : ''}</TableCell>
                      <TableCell>{line.quantity}</TableCell>
                      <TableCell data-testid={`bill-po-price-${line.goodsReceiptLineId}`}>
                        {foreign
                          ? (line.foreignPoUnitPrice != null ? formatFcPrice(bill.currencyCode ?? 'BDT', line.foreignPoUnitPrice) : '—')
                          : (line.poUnitPrice != null ? formatMoney(line.poUnitPrice) : '—')}
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          data-help="procurement.billEdit.field.linePrice"
                          error={!(billPrice > 0)}
                          value={Number.isFinite(billPrice) ? billPrice : ''}
                          onChange={e => {
                            const v = e.target.value;
                            setPrices(prev => ({
                              ...prev,
                              [line.goodsReceiptLineId]: v === '' ? NaN : Number(v),
                            }));
                          }}
                          inputProps={{ step: 'any', min: 0, 'aria-label': `bill-unit-price-${line.goodsReceiptLineId}` }}
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                      {showTaxCodes && (
                        <TableCell data-help="procurement.billEdit.field.lineTaxCode">
                          <TaxCodeSelect
                            data-testid={`bill-taxcode-${line.goodsReceiptLineId}`}
                            lookup={taxLookup}
                            side="INPUT"
                            value={codes[line.goodsReceiptLineId]}
                            onChange={cid => setCodes(prev => ({ ...prev, [line.goodsReceiptLineId]: cid }))}
                          />
                        </TableCell>
                      )}
                      <TableCell>
                        {Number.isFinite(billPrice)
                          ? (foreign && bill.currencyCode ? formatFc(bill.currencyCode, billPrice * line.quantity) : (billPrice * line.quantity).toFixed(4))
                          : '—'}
                      </TableCell>
                      {showTaxCodes && (
                        <TableCell>
                          {codes[line.goodsReceiptLineId] && Number.isFinite(billPrice)
                            ? previewTax(billPrice * line.quantity,
                              taxLookup?.codes?.find(c => c.id === codes[line.goodsReceiptLineId])?.effectiveRatePct ?? 0).toFixed(2)
                            : '—'}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
        </ScrollableTable>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button data-help="procurement.billEdit.save" variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save Changes'}
        </Button>
        <Button data-help="procurement.billEdit.cancel" variant="outlined" onClick={() => navigate(`/procurement/supplier-bills/${bill.id}`)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SupplierBillEdit;
